import type {
  AnnouncementAuthorSignature,
  AnnouncementBannerItem,
  AnnouncementContractIssue,
  AnnouncementListDecodeResult,
  AnnouncementMeta,
  ApiError,
  DecodeResult,
  PublicAnnouncement,
} from "./types"
import { ANNOUNCEMENT_AUTHOR_SIGNATURE_MAX_CODEPOINTS } from "./types"

type JsonRecord = Record<string, unknown>

export class AnnouncementContractError extends Error {
  constructor() {
    super("announcement contract violation")
    this.name = "AnnouncementContractError"
  }
}

function isRecord(value: unknown): value is JsonRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function isLevel(value: unknown): value is PublicAnnouncement["level"] {
  return value === "normal" || value === "important" || value === "emergency"
}

function isPinnedOrder(value: unknown): value is PublicAnnouncement["pinned_order"] {
  return value === null || value === 0 || value === 1 || value === 2
}

export function isAnnouncementAuthorSignature(value: unknown): value is AnnouncementAuthorSignature {
  return (
    typeof value === "string" &&
    value.length <= 2 * ANNOUNCEMENT_AUTHOR_SIGNATURE_MAX_CODEPOINTS &&
    value.trim().length > 0 &&
    Array.from(value).length <= ANNOUNCEMENT_AUTHOR_SIGNATURE_MAX_CODEPOINTS &&
    !/[\p{Cc}\p{Cf}]/u.test(value)
  )
}

function isPositiveSafeInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value > 0
}

function isNonNegativeSafeInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0
}

function isUtcInstant(value: unknown): value is string {
  if (typeof value !== "string") {
    return false
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d{1,9})?Z$/.exec(value)
  if (!match) {
    return false
  }
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const hour = Number(match[4])
  const minute = Number(match[5])
  const second = Number(match[6])
  if (month < 1 || month > 12 || hour > 23 || minute > 59 || second > 59) {
    return false
  }
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)
  const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
  return day >= 1 && day <= (days[month - 1] ?? 0)
}

function isNullableUtcInstant(value: unknown): value is string | null {
  return value === null || isUtcInstant(value)
}

export function isPublicAnnouncement(value: unknown): value is PublicAnnouncement {
  if (!isRecord(value) || !isPinnedOrder(value.pinned_order)) {
    return false
  }
  const expectedPinned = value.pinned_order !== null
  const expectedBanner = value.pinned_order === 0 || value.pinned_order === 1
  return (
    isPositiveSafeInteger(value.id) &&
    typeof value.title === "string" &&
    typeof value.body_md === "string" &&
    typeof value.body_html === "string" &&
    isLevel(value.level) &&
    value.is_pinned === expectedPinned &&
    value.is_banner === expectedBanner &&
    (value.author_signature === undefined || isAnnouncementAuthorSignature(value.author_signature)) &&
    (value.article_unread_revision === undefined || isNonNegativeSafeInteger(value.article_unread_revision)) &&
    isNullableUtcInstant(value.published_at) &&
    isUtcInstant(value.updated_at)
  )
}

function successData(payload: unknown): unknown {
  if (!isRecord(payload) || payload.ok !== true || !("data" in payload)) {
    throw new AnnouncementContractError()
  }
  return payload.data
}

function decodeAnnouncementArray(payload: unknown): DecodeResult<PublicAnnouncement[]> {
  const data = successData(payload)
  if (!Array.isArray(data)) {
    throw new AnnouncementContractError()
  }
  const announcements: PublicAnnouncement[] = []
  const issues: AnnouncementContractIssue[] = []
  data.forEach((value, index) => {
    if (isPublicAnnouncement(value)) {
      announcements.push(value)
    } else {
      issues.push({ code: "invalid_item", path: `data[${index}]` })
    }
  })
  return { data: announcements, issues }
}

export function decodeAnnouncements(payload: unknown): AnnouncementListDecodeResult {
  const decoded = decodeAnnouncementArray(payload)
  if (!isRecord(payload)) {
    throw new AnnouncementContractError()
  }
  const snapshotRevision = payload.article_unread_revision
  if (snapshotRevision !== undefined && !isNonNegativeSafeInteger(snapshotRevision)) {
    throw new AnnouncementContractError()
  }
  return snapshotRevision === undefined ? decoded : { ...decoded, article_unread_revision: snapshotRevision }
}

export function decodeAnnouncement(payload: unknown): PublicAnnouncement {
  const data = successData(payload)
  if (!isPublicAnnouncement(data)) {
    throw new AnnouncementContractError()
  }
  return data
}

export function decodeBanners(payload: unknown): DecodeResult<AnnouncementBannerItem[]> {
  const data = successData(payload)
  if (!Array.isArray(data)) {
    throw new AnnouncementContractError()
  }
  const banners: AnnouncementBannerItem[] = []
  const issues: AnnouncementContractIssue[] = []
  data.forEach((value, index) => {
    if (!isPublicAnnouncement(value) || (value.pinned_order !== 0 && value.pinned_order !== 1)) {
      issues.push({ code: "invalid_item", path: `data[${index}]` })
      return
    }
    const { id, title, body_html, level, pinned_order, published_at } = value
    banners.push({ id, title, body_html, level, pinned_order, published_at })
  })
  return { data: banners, issues }
}

export function decodeAnnouncementMeta(payload: unknown): AnnouncementMeta {
  const data = successData(payload)
  if (!isRecord(data)) {
    throw new AnnouncementContractError()
  }
  const articleRevision = data.article_revision === undefined ? 0 : data.article_revision
  const articleUnreadRevision = data.article_unread_revision === undefined ? 0 : data.article_unread_revision
  if (
    !isNullableUtcInstant(data.article) ||
    typeof articleRevision !== "number" ||
    !Number.isSafeInteger(articleRevision) ||
    articleRevision < 0 ||
    typeof articleUnreadRevision !== "number" ||
    !Number.isSafeInteger(articleUnreadRevision) ||
    articleUnreadRevision < 0 ||
    typeof data.banner !== "number" ||
    !Number.isSafeInteger(data.banner) ||
    data.banner < 0
  ) {
    throw new AnnouncementContractError()
  }
  return {
    article: data.article,
    article_revision: articleRevision,
    article_unread_revision: articleUnreadRevision,
    banner: data.banner,
  }
}

export function decodeAnnouncementApiError(payload: unknown): ApiError {
  if (
    !isRecord(payload) ||
    payload.ok !== false ||
    !isRecord(payload.error) ||
    (payload.error.code !== "bad_request" && payload.error.code !== "not_found" && payload.error.code !== "internal") ||
    typeof payload.error.message !== "string"
  ) {
    throw new AnnouncementContractError()
  }
  return payload as unknown as ApiError
}
